/**
 * AI Tools for Hub (Communications)
 *
 * Tools that allow Marta to interact with messages, calls, emails, and videocalls via GraphQL.
 */

import type { ToolDefinition } from './types';
import { graphqlQuery, graphqlMutation } from '../../database/graphqlClient';

// =============================================================================
// FIELD DEFINITIONS
// =============================================================================

const MESSAGE_FIELDS = `id channel fromId toId subject content direction projectId read readAt createdAt`;
const CALL_FIELDS = `id contactId phoneNumber direction status durationSeconds summary projectId startedAt endedAt`;
const EMAIL_FIELDS = `id fromAddress fromName toAddresses ccAddresses subject bodyText direction summary category contactId projectId read starred archived receivedAt createdAt`;
const VIDEOCALL_FIELDS = `id title description meetingUrl provider scheduledStart scheduledDuration startedAt endedAt recordingUrl summary projectId status createdBy createdAt`;
const HUB_STATS_FIELDS = `totalContacts totalCalls totalEmails totalMessages unreadEmails unreadMessages scheduledVideocalls`;

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const QUERIES = {
  messages: `query ListMessages { messages { ${MESSAGE_FIELDS} } }`,
  calls: `query ListCalls { calls { ${CALL_FIELDS} } }`,
  call: `query GetCall($id: ID!) { call(id: $id) { ${CALL_FIELDS} } }`,
  emails: `query ListEmails { emails { ${EMAIL_FIELDS} } }`,
  email: `query GetEmail($id: ID!) { email(id: $id) { ${EMAIL_FIELDS} } }`,
  videocalls: `query ListVideocalls { videocalls { ${VIDEOCALL_FIELDS} } }`,
  videocall: `query GetVideocall($id: ID!) { videocall(id: $id) { ${VIDEOCALL_FIELDS} } }`,
  hubActivity: `query HubActivity { hubActivity { ${MESSAGE_FIELDS} ${EMAIL_FIELDS} ${CALL_FIELDS} } }`,
  hubStats: `query HubStats { hubStats { ${HUB_STATS_FIELDS} } }`,
};

const MUTATIONS = {
  sendMessage: `mutation SendMessage($input: MessageInput!) { sendMessage(input: $input) { ${MESSAGE_FIELDS} } }`,
  deleteHubMessage: `mutation DeleteMessage($id: ID!) { deleteHubMessage(id: $id) }`,
  createCall: `mutation CreateCall($input: CallInput!) { createCall(input: $input) { ${CALL_FIELDS} } }`,
  updateCall: `mutation UpdateCall($id: ID!, $input: CallInput!) { updateCall(id: $id, input: $input) { ${CALL_FIELDS} } }`,
  deleteCall: `mutation DeleteCall($id: ID!) { deleteCall(id: $id) }`,
  sendEmail: `mutation SendEmail($input: EmailInput!) { sendEmail(input: $input) { ${EMAIL_FIELDS} } }`,
  deleteEmail: `mutation DeleteEmail($id: ID!) { deleteEmail(id: $id) }`,
  createVideocall: `mutation CreateVideocall($input: VideocallInput!) { createVideocall(input: $input) { ${VIDEOCALL_FIELDS} } }`,
  updateVideocall: `mutation UpdateVideocall($id: ID!, $input: VideocallInput!) { updateVideocall(id: $id, input: $input) { ${VIDEOCALL_FIELDS} } }`,
  deleteVideocall: `mutation DeleteVideocall($id: ID!) { deleteVideocall(id: $id) }`,
};

// =============================================================================
// TOOL DEFINITIONS
// =============================================================================

export const listMessagesTool: ToolDefinition = {
  name: 'list_messages',
  description: 'Elenca i messaggi. Può filtrare per canale (whatsapp, telegram, sms), direzione, o stato lettura.',
  category: 'hub',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.messages,
  parameters: {
    type: 'object',
    properties: {
      channel: {
        type: 'string',
        description: 'Filtra per canale',
        enum: ['whatsapp', 'telegram', 'sms', 'other'],
      },
      direction: {
        type: 'string',
        description: 'Filtra per direzione',
        enum: ['in', 'out'],
      },
      unread: {
        type: 'boolean',
        description: 'Mostra solo messaggi non letti',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const listCallsTool: ToolDefinition = {
  name: 'list_calls',
  description: 'Elenca le chiamate registrate. Può filtrare per stato o direzione.',
  category: 'hub',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.calls,
  parameters: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        description: 'Filtra per stato chiamata',
        enum: ['completed', 'missed', 'rejected', 'in_progress'],
      },
      direction: {
        type: 'string',
        description: 'Filtra per direzione',
        enum: ['in', 'out'],
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const listEmailsTool: ToolDefinition = {
  name: 'list_emails',
  description: 'Elenca le email. Può filtrare per stato lettura, starred, o direzione.',
  category: 'hub',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.emails,
  parameters: {
    type: 'object',
    properties: {
      unread: {
        type: 'boolean',
        description: 'Mostra solo email non lette',
      },
      starred: {
        type: 'boolean',
        description: 'Mostra solo email starred',
      },
      direction: {
        type: 'string',
        description: 'Filtra per direzione',
        enum: ['in', 'out'],
      },
      archived: {
        type: 'boolean',
        description: 'Mostra solo email archiviate',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const getEmailTool: ToolDefinition = {
  name: 'get_email',
  description: 'Ottiene i dettagli completi di una email specifica.',
  category: 'hub',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.email,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della email',
      },
    },
    required: ['id'],
  },
};

export const listVideocallsTool: ToolDefinition = {
  name: 'list_videocalls',
  description: 'Elenca le videochiamate. Può filtrare per stato.',
  category: 'hub',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.videocalls,
  parameters: {
    type: 'object',
    properties: {
      status: {
        type: 'string',
        description: 'Filtra per stato',
        enum: ['scheduled', 'in_progress', 'completed', 'cancelled'],
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const sendMessageTool: ToolDefinition = {
  name: 'send_message',
  description: 'Invia un messaggio tramite un canale (WhatsApp, Telegram, SMS).',
  category: 'hub',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.sendMessage,
  parameters: {
    type: 'object',
    properties: {
      channel: {
        type: 'string',
        description: 'Canale di invio',
        enum: ['whatsapp', 'telegram', 'sms', 'other'],
      },
      toId: {
        type: 'string',
        description: 'ID del destinatario (contatto)',
      },
      content: {
        type: 'string',
        description: 'Contenuto del messaggio',
      },
      subject: {
        type: 'string',
        description: 'Oggetto (opzionale)',
      },
      projectId: {
        type: 'string',
        description: 'ID progetto associato (opzionale)',
      },
    },
    required: ['channel', 'toId', 'content'],
  },
};

export const deleteMessageTool: ToolDefinition = {
  name: 'delete_message',
  description: 'Elimina un messaggio. ATTENZIONE: Questa azione è irreversibile.',
  category: 'hub',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteHubMessage,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del messaggio da eliminare',
      },
    },
    required: ['id'],
  },
};

export const createCallTool: ToolDefinition = {
  name: 'create_call',
  description: 'Registra una chiamata telefonica.',
  category: 'hub',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createCall,
  parameters: {
    type: 'object',
    properties: {
      contactId: {
        type: 'string',
        description: 'ID del contatto',
      },
      phoneNumber: {
        type: 'string',
        description: 'Numero di telefono',
      },
      direction: {
        type: 'string',
        description: 'Direzione chiamata',
        enum: ['in', 'out'],
      },
      status: {
        type: 'string',
        description: 'Stato chiamata',
        enum: ['completed', 'missed', 'rejected', 'in_progress'],
      },
      durationSeconds: {
        type: 'number',
        description: 'Durata in secondi',
      },
      summary: {
        type: 'string',
        description: 'Riassunto della chiamata',
      },
      projectId: {
        type: 'string',
        description: 'ID progetto associato (opzionale)',
      },
      startedAt: {
        type: 'string',
        description: 'Data/ora inizio (ISO 8601)',
      },
    },
    required: ['phoneNumber', 'direction', 'status'],
  },
};

export const sendEmailTool: ToolDefinition = {
  name: 'send_email',
  description: 'Invia una email.',
  category: 'hub',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.sendEmail,
  parameters: {
    type: 'object',
    properties: {
      toAddresses: {
        type: 'array',
        items: { type: 'string' },
        description: 'Indirizzi email destinatari',
      },
      ccAddresses: {
        type: 'array',
        items: { type: 'string' },
        description: 'Indirizzi email CC (opzionale)',
      },
      subject: {
        type: 'string',
        description: 'Oggetto email',
      },
      bodyText: {
        type: 'string',
        description: 'Corpo email (testo)',
      },
      contactId: {
        type: 'string',
        description: 'ID contatto associato (opzionale)',
      },
      projectId: {
        type: 'string',
        description: 'ID progetto associato (opzionale)',
      },
    },
    required: ['toAddresses', 'subject', 'bodyText'],
  },
};

export const deleteEmailTool: ToolDefinition = {
  name: 'delete_email',
  description: 'Elimina una email. ATTENZIONE: Questa azione è irreversibile.',
  category: 'hub',
  operation: 'delete',
  requiresConfirmation: true,
  graphqlOperation: MUTATIONS.deleteEmail,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della email da eliminare',
      },
    },
    required: ['id'],
  },
};

export const createVideocallTool: ToolDefinition = {
  name: 'create_videocall',
  description: 'Crea/programma una videochiamata.',
  category: 'hub',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createVideocall,
  parameters: {
    type: 'object',
    properties: {
      title: {
        type: 'string',
        description: 'Titolo videochiamata',
      },
      description: {
        type: 'string',
        description: 'Descrizione (opzionale)',
      },
      meetingUrl: {
        type: 'string',
        description: 'URL meeting (opzionale, se generato da sistema)',
      },
      provider: {
        type: 'string',
        description: 'Provider videochiamata',
        enum: ['zoom', 'meet', 'teams', 'other'],
      },
      scheduledStart: {
        type: 'string',
        description: 'Data/ora inizio programmato (ISO 8601)',
      },
      scheduledDuration: {
        type: 'number',
        description: 'Durata prevista in minuti',
      },
      projectId: {
        type: 'string',
        description: 'ID progetto associato (opzionale)',
      },
      status: {
        type: 'string',
        description: 'Stato iniziale',
        enum: ['scheduled', 'in_progress', 'completed', 'cancelled'],
        default: 'scheduled',
      },
    },
    required: ['title', 'scheduledStart'],
  },
};

export const getHubStatsTool: ToolDefinition = {
  name: 'get_hub_stats',
  description: 'Ottiene le statistiche del Hub (contatti, chiamate, email, messaggi).',
  category: 'hub',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.hubStats,
  parameters: {
    type: 'object',
    properties: {},
  },
};

// =============================================================================
// TOOL EXECUTION FUNCTIONS
// =============================================================================

interface Message {
  id: string;
  channel?: string;
  fromId?: string;
  toId?: string;
  subject?: string;
  content?: string;
  direction?: string;
  projectId?: string;
  read?: boolean;
  readAt?: string;
  createdAt?: string;
  [key: string]: unknown;
}

interface Call {
  id: string;
  contactId?: string;
  phoneNumber?: string;
  direction?: string;
  status?: string;
  durationSeconds?: number;
  summary?: string;
  projectId?: string;
  startedAt?: string;
  endedAt?: string;
  [key: string]: unknown;
}

interface Email {
  id: string;
  fromAddress?: string;
  fromName?: string;
  toAddresses?: string[];
  ccAddresses?: string[];
  subject?: string;
  bodyText?: string;
  direction?: string;
  summary?: string;
  category?: string;
  contactId?: string;
  projectId?: string;
  read?: boolean;
  starred?: boolean;
  archived?: boolean;
  receivedAt?: string;
  createdAt?: string;
  [key: string]: unknown;
}

interface Videocall {
  id: string;
  title?: string;
  description?: string;
  meetingUrl?: string;
  provider?: string;
  scheduledStart?: string;
  scheduledDuration?: number;
  startedAt?: string;
  endedAt?: string;
  recordingUrl?: string;
  summary?: string;
  projectId?: string;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  [key: string]: unknown;
}

interface HubStats {
  totalContacts?: number;
  totalCalls?: number;
  totalEmails?: number;
  totalMessages?: number;
  unreadEmails?: number;
  unreadMessages?: number;
  scheduledVideocalls?: number;
  [key: string]: unknown;
}

export async function executeListMessages(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ messages: Message[] }>(QUERIES.messages);
  let messages = result.messages || [];

  // Apply channel filter
  if (params.channel) {
    messages = messages.filter(m => m.channel === params.channel);
  }

  // Apply direction filter
  if (params.direction) {
    messages = messages.filter(m => m.direction === params.direction);
  }

  // Apply unread filter
  if (params.unread !== undefined) {
    messages = messages.filter(m => params.unread ? !m.read : m.read);
  }

  // Sort by createdAt descending
  messages.sort((a, b) => new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  messages = messages.slice(0, limit);

  return { messages, count: messages.length };
}

export async function executeListCalls(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ calls: Call[] }>(QUERIES.calls);
  let calls = result.calls || [];

  // Apply status filter
  if (params.status) {
    calls = calls.filter(c => c.status === params.status);
  }

  // Apply direction filter
  if (params.direction) {
    calls = calls.filter(c => c.direction === params.direction);
  }

  // Sort by startedAt descending
  calls.sort((a, b) => new Date(b.startedAt || '').getTime() - new Date(a.startedAt || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  calls = calls.slice(0, limit);

  return { calls, count: calls.length };
}

export async function executeListEmails(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ emails: Email[] }>(QUERIES.emails);
  let emails = result.emails || [];

  // Apply unread filter
  if (params.unread !== undefined) {
    emails = emails.filter(e => params.unread ? !e.read : e.read);
  }

  // Apply starred filter
  if (params.starred !== undefined) {
    emails = emails.filter(e => e.starred === params.starred);
  }

  // Apply direction filter
  if (params.direction) {
    emails = emails.filter(e => e.direction === params.direction);
  }

  // Apply archived filter
  if (params.archived !== undefined) {
    emails = emails.filter(e => e.archived === params.archived);
  }

  // Sort by receivedAt descending
  emails.sort((a, b) => new Date(b.receivedAt || b.createdAt || '').getTime() - new Date(a.receivedAt || a.createdAt || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  emails = emails.slice(0, limit);

  return { emails, count: emails.length };
}

export async function executeGetEmail(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ email: Email }>(QUERIES.email, { id: params.id });

  if (!result.email) {
    return { error: 'Email non trovata', found: false };
  }

  return { email: result.email, found: true };
}

export async function executeListVideocalls(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ videocalls: Videocall[] }>(QUERIES.videocalls);
  let videocalls = result.videocalls || [];

  // Apply status filter
  if (params.status) {
    videocalls = videocalls.filter(v => v.status === params.status);
  }

  // Sort by scheduledStart descending
  videocalls.sort((a, b) => new Date(b.scheduledStart || '').getTime() - new Date(a.scheduledStart || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  videocalls = videocalls.slice(0, limit);

  return { videocalls, count: videocalls.length };
}

export async function executeSendMessage(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    channel: params.channel,
    toId: params.toId,
    content: params.content,
    subject: params.subject || null,
    projectId: params.projectId || null,
    direction: 'out',
  };

  const result = await graphqlMutation<{ sendMessage: Message }>(MUTATIONS.sendMessage, { input });
  return result.sendMessage;
}

export async function executeDeleteMessage(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteHubMessage, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeCreateCall(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    contactId: params.contactId || null,
    phoneNumber: params.phoneNumber,
    direction: params.direction,
    status: params.status,
    durationSeconds: params.durationSeconds || null,
    summary: params.summary || null,
    projectId: params.projectId || null,
    startedAt: params.startedAt || null,
  };

  const result = await graphqlMutation<{ createCall: Call }>(MUTATIONS.createCall, { input });
  return result.createCall;
}

export async function executeSendEmail(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    toAddresses: params.toAddresses,
    ccAddresses: params.ccAddresses || [],
    subject: params.subject,
    bodyText: params.bodyText,
    contactId: params.contactId || null,
    projectId: params.projectId || null,
    direction: 'out',
  };

  const result = await graphqlMutation<{ sendEmail: Email }>(MUTATIONS.sendEmail, { input });
  return result.sendEmail;
}

export async function executeDeleteEmail(params: Record<string, unknown>): Promise<unknown> {
  await graphqlMutation(MUTATIONS.deleteEmail, { id: params.id });
  return { deleted: true, id: params.id };
}

export async function executeCreateVideocall(params: Record<string, unknown>): Promise<unknown> {
  const input = {
    title: params.title,
    description: params.description || null,
    meetingUrl: params.meetingUrl || null,
    provider: params.provider || null,
    scheduledStart: params.scheduledStart,
    scheduledDuration: params.scheduledDuration || null,
    projectId: params.projectId || null,
    status: params.status || 'scheduled',
  };

  const result = await graphqlMutation<{ createVideocall: Videocall }>(MUTATIONS.createVideocall, { input });
  return result.createVideocall;
}

export async function executeGetHubStats(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ hubStats: HubStats }>(QUERIES.hubStats);
  return result.hubStats || {};
}

// =============================================================================
// EXPORTS
// =============================================================================

export const hubTools: ToolDefinition[] = [
  listMessagesTool,
  listCallsTool,
  listEmailsTool,
  getEmailTool,
  listVideocallsTool,
  sendMessageTool,
  deleteMessageTool,
  createCallTool,
  sendEmailTool,
  deleteEmailTool,
  createVideocallTool,
  getHubStatsTool,
];

export const hubToolExecutors: Record<string, (params: Record<string, unknown>) => Promise<unknown>> = {
  list_messages: executeListMessages,
  list_calls: executeListCalls,
  list_emails: executeListEmails,
  get_email: executeGetEmail,
  list_videocalls: executeListVideocalls,
  send_message: executeSendMessage,
  delete_message: executeDeleteMessage,
  create_call: executeCreateCall,
  send_email: executeSendEmail,
  delete_email: executeDeleteEmail,
  create_videocall: executeCreateVideocall,
  get_hub_stats: executeGetHubStats,
};
